/**
 * This controller manages the broker permissions information.
 * Based on Flutter PermissionBrokerModelController
 * @author Based on Flutter implementation by Ali Nour Eldin
 */
class PermissionBrokerModelController {
  constructor(user = null) {
    this.permissionsList = [];
    
    // Check for PERMISSIONS (uppercase) or permissions (lowercase) for compatibility
    const permissionsData = user?.PERMISSIONS || user?.permissions;
    
    if (permissionsData) {
      try {
        let permissions;
        
        // Handle both string (JSON) and object formats
        if (typeof permissionsData === 'string') {
          permissions = JSON.parse(permissionsData);
        } else {
          permissions = permissionsData;
        }
        
        if (permissions && permissions.BROKER !== undefined) {
          // Handle null, empty string, or empty array (BROKER can be "" or null when empty)
          if (permissions.BROKER === null || permissions.BROKER === '' || !Array.isArray(permissions.BROKER)) {
            this.permissionsList = [];
          } else {
            this.permissionsList = permissions.BROKER || [];
          }
        } else {
          this.permissionsList = [];
        }
      } catch (error) {
        this.permissionsList = [];
      }
    }
  }

  static fromDevice(device = null) {
    const instance = new PermissionBrokerModelController();
    
    // Check for PERMISSION (uppercase, singular) or permission (lowercase) for compatibility
    const permissionData = device?.PERMISSION || device?.permission;
    
    if (permissionData) {
      try {
        let permissions;
        
        // Handle both string (JSON) and object formats
        if (typeof permissionData === 'string') {
          permissions = JSON.parse(permissionData);
        } else {
          permissions = permissionData;
        }
        
        if (permissions && permissions.BROKER !== undefined) {
          // Handle null, empty string, or empty array (BROKER can be "" or null when empty)
          if (permissions.BROKER === null || permissions.BROKER === '' || !Array.isArray(permissions.BROKER)) {
            instance.permissionsList = [];
          } else {
            instance.permissionsList = permissions.BROKER || [];
          }
        } else {
          instance.permissionsList = [];
        }
      } catch (error) {
        instance.permissionsList = [];
      }
    } else {
      instance.permissionsList = [];
    }
    
    return instance;
  }

  savePermission() {
    return JSON.stringify({ BROKER: this.permissionsList });
  }

  getPermission() {
    return { BROKER: this.permissionsList };
  }

  canDeposit() {
    return (
      (this.permissionsList?.includes(PermissionBrokerModelController.PER__CAN_DEPOSIT_ALL) || false) ||
      (this.permissionsList?.includes(PermissionBrokerModelController.PER__CAN_DEPOSIT_CLIENT) || false)
    );
  }

  canWithdraw() {
    return (
      (this.permissionsList?.includes(PermissionBrokerModelController.PER__CAN_WITHDRAWAL_ALL) || false) ||
      (this.permissionsList?.includes(PermissionBrokerModelController.PER__CAN_WITHDRAWAL_CLIENT) || false)
    );
  }

  // Permission constants (static properties)
  static PER__CAN_DEPOSIT_CLIENT = 'CAN_DEPOSIT_CLIENT';
  static PER__CAN_DEPOSIT_ALL = 'CAN_DEPOSIT_ALL';
  static PER__CAN_WITHDRAWAL_CLIENT = 'CAN_WITHDRAWAL_CLIENT';
  static PER__CAN_WITHDRAWAL_ALL = 'CAN_WITHDRAWAL_ALL';
  static PER__CAN_ENABLE_TRADING_ACCOUNT__TRADING = 'CAN_ENABLE_TRADING_ACCOUNT__TRADING';
  static PER__CAN_ENABLE_TRADING_ACCOUNT__ACCOUNT = 'CAN_ENABLE_TRADING_ACCOUNT__ACCOUNT';
  static PER__CAN_SEND_NOTIFICATION = 'CAN_SEND_NOTIFICATION';
  static PER__CAN_VIEW_CLIENT = 'CAN_VIEW_CLIENT';
  static PER__CAN_VIEW_CLIENT_TRADING_HISTORY = 'CAN_VIEW_CLIENT_TRADING_HISTORY';
  static PER__CAN_VIEW_CLIENT_TREE_LIST = 'CAN_VIEW_CLIENT_TREE_LIST';
  static PER__CAN_VIEW_BALANCE_EQUITY_CLIENTS = 'CAN_VIEW_BALANCE_EQUITY_CLIENTS';
  static PER__CAN_UPDATE_LEVERAGE = 'CAN_UPDATE_LEVERAGE';
  static PER__CAN_UPDATE_TRADING_GROUP = 'CAN_UPDATE_TRADING_GROUP';
  static PER__CAN_COLLECT_CASHIN = 'CAN_COLLECT_CASHIN';
  static PER__CAN_COLLECT_CASH_OUT = 'CAN_COLLECT_CASH_OUT';
  static PER__CAN_TRANSFER_CASHIN = 'CAN_TRANSFER_CASHIN';
  static PER__CAN_TRANSFER_CASH_OUT = 'CAN_TRANSFER_CASH_OUT';
  static PER__CAN_VIEW_TREE_CONTACT = 'CAN _VIEW_TREE_CONTACT';
  static PER__CAN_VIEW_BROKER_TOOLS = 'CAN_VIEW_BROKER_TOOLS';
  // When present in a broker's permission list, agent (broker) withdrawal is disabled.
  static PER__AGENT_WITHDRAWAL_DISABLED = 'AGENT_WITHDRAWAL_DISABLED';

  // Helper method to check if permission exists
  hasPermission(permission) {
    return this.permissionsList && this.permissionsList.includes(permission);
  }
}

export default PermissionBrokerModelController;
